import React, { useEffect, useState } from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import * as campaignActions from 'actions/campaign';
import * as routerActions from 'router/actions';
import { useI18n } from 'components/I18n';
import {
  PaymentProviderFooter,
  AdyenResultHandler,
} from 'components/Payment';
import {
  Box,
  Heading,
  Text,
  Metadata,
  SecondaryButton,
  PrimaryButton,
} from 'components';
import NoOffersErrorPage from 'components/ErrorPage/NoOffersErrorPage';
import { RouterLocation } from 'router';
import {
  AppDispatch,
  RootState,
} from 'reducers';
import { Offer } from 'types';
import { flattenEdges } from 'utils/helpers';
import ErrorPage, { ERROR_ICONS } from 'components/ErrorPage/ErrorPage';
import PromoCodeModal from '../PromoCodeModal';
import { useGetOffersToChangeQuery } from '../getOffersToChange.generated';
import OfferComponent from './Offer';
import { Container } from './styles';
import logger from 'utils/logger';
import { OFFER_TYPES } from 'utils/constants';
import PromocodeDifferentOfferModal from '../PromocodeDifferentOffer';
import { getOffer, mostExpensiveOffer } from './utils';

type Props = {
  location: RouterLocation,
};

// Select offer component to change user's entitlement offer. Can be opened from My Plan page
// Route: '/offers/change/:id' where id is entitlement's offerId
function SelectOffersChange({ location }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const i18n = useI18n();

  const { id } = location.params!;
  const { promoCode } = location.query!;

  const {
    withPromoCodes,
  } = useSelector((state: RootState) => ({
    withPromoCodes: (
      state.settings.features.payment?.allowPromoCodes
      && state.settings.features.payment?.allowUpgradePromoCodes
    ) || false,
  }), shallowEqual);

  const [selectedOfferId, setSelectedOfferId] = React.useState<string | undefined>();
  const [bestValueOfferId, setBestValueOfferId] = React.useState<string | undefined>();

  const [showModal, setShowModal] = useState(false);
  const [confirmModalOffer, setConfirmModalOffer] = React.useState<undefined | string>();
  const [confirmModalPromo, setConfirmModalPromo] = React.useState<undefined | string>();
  const [initPromoValidation, setInitPromoValidation] = React.useState(!!promoCode);

  const {
    data,
    error: apolloError,
  } = useGetOffersToChangeQuery({ variables: { offerId: id } });

  const originalOffer = data?.viewer?.offer as Offer;
  // find availableOffers from subscriptions.replacements API
  // only UPGRADE ones for now
  const availableOffers = flattenEdges(
    data?.viewer?.subscriptions?.edges?.find(s => s?.node.offerId === id)?.node.replacements || [],
  ).filter((s: Offer) => s?.subscribeKind === 'UPGRADE') as Offer[];

  const validatePromocode = async (newPromoCode: string) => {
    const expectedOfferIds = availableOffers.map((offer: Offer) => offer.id);

    try {
      const { offerIds } = await dispatch(campaignActions.getCampaign(newPromoCode));

      const matchingOfferIds = expectedOfferIds.filter((oId: string) => offerIds.includes(oId));

      if (!matchingOfferIds.length) {
        throw { errorId: 'promoCode.notValid' };
      }

      const offerId = matchingOfferIds[0];

      const { valid, errorId } = await dispatch(campaignActions.validatePromocodeToOffer(offerId, newPromoCode));

      if (!valid) {
        const error = errorId === 'promoCode.notValidForOffer' ? 'promoCode.notValid' : errorId;
        throw { errorId: error };
      }

      if (selectedOfferId && selectedOfferId !== offerId) {
        setShowModal(false);
        setConfirmModalOffer(offerId);
        setConfirmModalPromo(newPromoCode);
      } else {
        dispatch(routerActions.push({
          name: 'checkout-upgrade',
          params: { id: offerId, originalOfferId: originalOffer.id },
          query: { promoCode: newPromoCode },
        }));
      }
    } catch (e: any) {
      setInitPromoValidation(false);
      throw i18n.formatText(
        e?.errorId || campaignActions.extractPromocodeValidationError(JSON.stringify(e)),
        { promoCode: newPromoCode },
      );
    }
  };

  const hideModal = () => {
    setShowModal(false);
  };

  const onOfferClick = (offerId: string) => {
    setSelectedOfferId(offerId);
  };

  const openPayment = (offerId: string, promo?: string) => {
    dispatch(routerActions.push({
      name: 'checkout-upgrade',
      params: { id: offerId, originalOfferId: originalOffer.id },
      query: { ...( promo ? { promoCode: promo } : null ) },
    }));
  };

  useEffect(() => {
    if (
      promoCode
      && withPromoCodes
      && availableOffers?.length
    ) {
      void validatePromocode(promoCode as string).catch((e)=> {
        logger.warn('Promocode validation failed', e);
      });
    }

    // auto select
    const mostExpensive = mostExpensiveOffer(availableOffers);
    if (mostExpensive) {
      setSelectedOfferId(mostExpensive.id);
      if (mostExpensive.__typename === OFFER_TYPES.SubscribeType) {
        setBestValueOfferId(mostExpensive.id);
      }
    }
  }, [availableOffers.length]);

  if (apolloError) {
    return (
        <ErrorPage button="retry" icon={ERROR_ICONS.NOT_FOUND}>
          <Text id="error" />
        </ErrorPage>
    );
  }

  if (!data || initPromoValidation) {
    return null;
  }

  if (!originalOffer || !availableOffers.length) {
    return (
        <NoOffersErrorPage hasEntitlement />
    );
  }

  return (
    <>
    <Container column mx="auto" xl-mt="mlarge" mt="large" maxWidth="100%" width="100%">
      <Metadata title="checkout.changeOffer" />

      <Box mb="medium">
        <AdyenResultHandler />
      </Box>

      <Box
        maxWidth="100%"
      >
        <Heading
          fontSize="sectionHeading"
          align="center"
          id="checkout.changeOffer"
        />

        <Box mt="xxlarge" row wrap columnGap="large">
          <OfferComponent
            key={originalOffer.id}
            offer={originalOffer}
            onClick={() => {}}
            disabled
            disableTrial
          />
          {availableOffers.map(offer => (
            <OfferComponent
              key={offer.id}
              offer={offer}
              onClick={() => onOfferClick(offer.id)}
              selected={offer.id === selectedOfferId}
              bestValue={offer.id === bestValueOfferId}
              disableTrial
            />
          ))}
        </Box>
      </Box>

      <Box mb="large">
        <Text id="payment.redeemPromoCode" />
      </Box>

      {withPromoCodes && showModal && (
        <PromoCodeModal
          initialValue={promoCode?.toString()}
          validatePromocode={validatePromocode}
          onClose={hideModal}
        />
      )}

      {confirmModalOffer && (
        <PromocodeDifferentOfferModal
          onDismiss={() => setConfirmModalOffer(undefined)}
          onConfirm={() => openPayment(confirmModalOffer, confirmModalPromo)}
          offerName={getOffer(confirmModalOffer, availableOffers)?.title}
        />
      )}

      <Box mb="small">
        <PaymentProviderFooter />
      </Box>
    </Container>

    <Box
      flexBox
      p="medium"
      xl-p="large"
      position="sticky"
      left="0"
      right="0"
      bottom="0"
      xs-column
      xs-alignItems="stretch"
      justifyContent="center"
      gap="large"
      width="100%"
      bg="page"
      wrap
      borderTop="divider"
    >
      {false && withPromoCodes && ( // temporary disable promo code button - MDM-25792
        <SecondaryButton
          variant="white"
          onClick={() => setShowModal(true)}
          className="e2e-promocode-cta"
        >
          <Text id="payment.promoCodeButton" />
        </SecondaryButton>
      )}
      <PrimaryButton
        onClick={selectedOfferId ? () => openPayment(selectedOfferId) : undefined}
        disabled={!selectedOfferId}
      >
        <Text id="changeOffer.continue" />
      </PrimaryButton>
    </Box>
    </>
  );
}

export default React.memo(SelectOffersChange);
